import {
  Briefcase,
  CalendarDays,
  Handshake,
  HelpCircle,
  MessageCircle,
  Newspaper,
  Scissors,
  Sparkles,
  Utensils,
  type LucideIcon,
} from "lucide-react";
import type { RestaurantSiteConfig } from "@/lib/restaurant-platform";
import { getBusinessVerticalForRestaurant } from "@/lib/tenant-capabilities";
import type { ContactInquiryType } from "@/lib/contact-lead-classification";

export type { ContactInquiryType };

export type ContactInquiryOption = {
  value: ContactInquiryType;
  label: string;
  icon: LucideIcon;
  description: string;
};

export type ContactQuickLink = {
  href: string;
  label: string;
  icon: LucideIcon;
  analyticsLocation: string;
};

export type ContactRoutingLink = {
  href: string;
  label: string;
  detail: string;
};

export type ContactTrustPoint = {
  title: string;
  body: string;
};

export type ContactFaq = {
  question: string;
  answer: string;
};

export type ContactLeadCopy = {
  pageIntro: string;
  infoCardTitle: string;
  smsConsentNouns: string;
  trustHeading: string;
  inquiryTypes: ContactInquiryOption[];
  quickLinks: ContactQuickLink[];
  routingLinks: ContactRoutingLink[];
  trustPoints: ContactTrustPoint[];
  faqs: ContactFaq[];
};

const baseInquiryOptions: ContactInquiryOption[] = [
  {
    value: "general",
    label: "General Question",
    icon: HelpCircle,
    description: "Questions and basic details",
  },
  {
    value: "feedback",
    label: "Feedback",
    icon: MessageCircle,
    description: "Share your experience",
  },
  {
    value: "partnership",
    label: "Partnership Inquiry",
    icon: Handshake,
    description: "Business collaborations",
  },
  {
    value: "press",
    label: "Press / Media",
    icon: Newspaper,
    description: "Media inquiries and interviews",
  },
  {
    value: "other",
    label: "Other",
    icon: Sparkles,
    description: "Anything else",
  },
];

function baseFaqs(
  siteConfig: RestaurantSiteConfig,
  shortName: string,
  phoneDisplay: string,
  email: string,
): ContactFaq[] {
  return [
    {
      question: `How do I contact ${shortName}?`,
      answer: `Use the contact form, call ${phoneDisplay}, or email ${email}.`,
    },
    {
      question: `Where is ${shortName} located?`,
      answer: `${siteConfig.name} is located at ${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}.`,
    },
  ];
}

export function buildContactLeadCopy(args: {
  siteConfig: RestaurantSiteConfig;
  shortName: string;
  phoneDisplay: string;
  email: string;
}): ContactLeadCopy {
  const { siteConfig, shortName, phoneDisplay, email } = args;
  const vertical = getBusinessVerticalForRestaurant(siteConfig.restaurantId);

  if (vertical.id === "barber_shop") {
    return {
      pageIntro:
        "Send an appointment question, service note, or follow-up request to the shop. For immediate help, call directly.",
      infoCardTitle: "Shop info",
      smsConsentNouns: "appointment, service, and support",
      trustHeading: "The right path for appointment questions",
      inquiryTypes: [
        {
          value: "general",
          label: "Appointment Question",
          icon: Scissors,
          description: "Booking and schedule questions",
        },
        {
          value: "feedback",
          label: "Client Feedback",
          icon: MessageCircle,
          description: "Share your visit",
        },
        ...baseInquiryOptions.slice(2),
      ],
      quickLinks: [
        {
          href: "/contact",
          label: "Book appointment",
          icon: Scissors,
          analyticsLocation: "contact_quick_links",
        },
        {
          href: "/about",
          label: "Services",
          icon: Sparkles,
          analyticsLocation: "contact_quick_links",
        },
        {
          href: "/about",
          label: "Barbers",
          icon: Handshake,
          analyticsLocation: "contact_quick_links",
        },
        {
          href: "/jobs",
          label: "Careers",
          icon: Briefcase,
          analyticsLocation: "contact_quick_links",
        },
      ],
      routingLinks: [
        {
          href: "#contact-form",
          label: "Appointment question",
          detail: "Use this form for booking, rescheduling, service, or barber preference questions.",
        },
        {
          href: "/about",
          label: "Services",
          detail: "Review available services before sending a booking note.",
        },
        {
          href: "/about",
          label: "Barbers",
          detail: "Use for barber, chair, and service-fit questions.",
        },
        {
          href: "/jobs",
          label: "Careers",
          detail: "Use for current roles, hiring questions, and shop opportunities.",
        },
        {
          href: "#contact-form",
          label: "General contact",
          detail: `Use this form, call ${phoneDisplay}, or email ${email}.`,
        },
      ],
      trustPoints: [
        {
          title: "Direct shop contact",
          body: `${siteConfig.name} receives these messages directly so appointment, service, and follow-up notes stay tied to the right shop.`,
        },
        {
          title: "Best path by need",
          body: `Use the quick links for appointments, services, barbers, and careers so the ${shortName} team gets useful context from the start.`,
        },
        {
          title: "Local details in one place",
          body: `Address, phone, email, hours, and map details reflect ${siteConfig.address.line1} in ${siteConfig.address.city}.`,
        },
      ],
      faqs: [
        ...baseFaqs(siteConfig, shortName, phoneDisplay, email),
        {
          question: `Can I ask ${shortName} about appointments or services?`,
          answer:
            "Yes. Use the contact form for booking, service, and barber questions.",
        },
        {
          question: `Can I apply for shop jobs at ${shortName}?`,
          answer:
            "Yes. Use the jobs page or contact form for current shop opportunities.",
        },
      ],
    };
  }

  return {
    pageIntro:
      "Send a question, note, or follow-up request to the restaurant team. For immediate help, call the restaurant directly.",
    infoCardTitle: "Restaurant info",
    smsConsentNouns: "event, catering, and support",
    trustHeading: "The right path for restaurant questions",
    inquiryTypes: [
      {
        ...baseInquiryOptions[0],
        description: "Questions about our restaurant",
      },
      {
        ...baseInquiryOptions[1],
        description: "Share your dining experience",
      },
      ...baseInquiryOptions.slice(2),
    ],
    quickLinks: [
      {
        href: "/reservations",
        label: "Make a reservation",
        icon: CalendarDays,
        analyticsLocation: "contact_quick_links",
      },
      {
        href: "/menu",
        label: "View menu",
        icon: Utensils,
        analyticsLocation: "contact_quick_links",
      },
      {
        href: "/private-events",
        label: "Private events",
        icon: Sparkles,
        analyticsLocation: "contact_quick_links",
      },
      {
        href: "/jobs",
        label: "Careers",
        icon: Briefcase,
        analyticsLocation: "contact_quick_links",
      },
    ],
    routingLinks: [
      {
        href: "/reservations",
        label: "Reservations",
        detail: "Book or change a table reservation.",
      },
      {
        href: "/menu",
        label: "Menu questions",
        detail: "Check dinner, pizza, brunch, drinks, catering, and happy hour details first.",
      },
      {
        href: "/private-events",
        label: "Private events",
        detail: "Use for birthdays, corporate meals, rehearsal dinners, showers, and hosted group plans.",
      },
      {
        href: "/catering",
        label: "Catering",
        detail: "Use for trays, office meals, watch parties, birthdays, and group-food quotes.",
      },
      {
        href: "/jobs",
        label: "Jobs",
        detail: "Apply through the current restaurant jobs path.",
      },
      {
        href: "#contact-form",
        label: "General contact",
        detail: `Use this form, call ${phoneDisplay}, or email ${email}.`,
      },
    ],
    trustPoints: [
      {
        title: "Direct restaurant contact",
        body: `${siteConfig.name} receives these messages directly so general questions, visit details, and follow-ups stay tied to the right restaurant.`,
      },
      {
        title: "Best path by need",
        body: `Use the quick links for reservations, private events, catering, and jobs so the ${shortName} team gets the right context from the start.`,
      },
      {
        title: "Local details in one place",
        body: `Address, phone, email, hours, and map details reflect ${siteConfig.address.line1} in ${siteConfig.address.city}.`,
      },
    ],
    faqs: [
      ...baseFaqs(siteConfig, shortName, phoneDisplay, email),
      {
        question: `Can I ask ${shortName} about private events or catering?`,
        answer:
          "Yes. The general contact form works for quick questions, and the dedicated private-event and catering forms collect group meal details.",
      },
      {
        question: `Can I apply for restaurant jobs at ${shortName}?`,
        answer:
          "Yes. Use the jobs page for current restaurant application details and the team will review the submission in the admin panel.",
      },
    ],
  };
}
